import type { BaseResponse, HttpClient } from '@/lib/http/HttpClient';
import type {
  GaEvaluationInput,
  GaExperimentResult,
  GaExperimentSummary,
} from '../types/ToolRecommenderTypes';

type FlexResponse = BaseResponse & Record<string, unknown>;

type LaunchSuccessResponse = BaseResponse & {
  experimentId: string;
  resultsStored: number;
  resultComparisons: Record<string, unknown>[];
};

export type GaLaunchOutcome =
  | { type: 'completed'; result: GaExperimentResult }
  | { type: 'timeout'; jobId: string; message: string }
  | { type: 'error'; message: string };

function snakeToCamel(s: string): string {
  return s.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
}

function normalizeCamel<T>(v: unknown): T {
  if (Array.isArray(v)) return v.map(normalizeCamel) as unknown as T;
  if (v !== null && typeof v === 'object') {
    return Object.fromEntries(
      Object.entries(v as Record<string, unknown>).map(([k, val]) => [
        snakeToCamel(k),
        normalizeCamel(val),
      ])
    ) as T;
  }
  return v as T;
}

function extractList(d: unknown, ...keys: string[]): unknown[] {
  if (Array.isArray(d)) return d;
  if (d && typeof d === 'object') {
    const obj = d as Record<string, unknown>;
    for (const key of keys) {
      if (Array.isArray(obj[key])) return obj[key] as unknown[];
    }
  }
  return [];
}

const JOB_POLL_INTERVAL_MS = 3000;
/** How long a deferred read is followed before giving up: a GA run can take many minutes. */
const JOB_POLL_TIMEOUT_MS = 15 * 60 * 1000;

/**
 * The orchestrator answers `202 { name, message, jobId }` when the worker has not replied within
 * `TOOL_RECOMMENDER_TIMEOUT_SECONDS` (3 s); the job still completes later. Until 2026-09-22 the
 * reads below took that body for an empty result, so the history vanished while a run was in
 * progress (the worker answered one job at a time). Returns the job to wait for, or null.
 */
function deferredJobId(body: unknown): string | null {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null;
  const { jobId, experimentId, experiments } = body as Record<string, unknown>;
  if (typeof jobId !== 'string') return null;
  if (typeof experimentId === 'string' || Array.isArray(experiments)) return null;
  return jobId;
}

function abortError(): Error {
  return new DOMException('The request was aborted', 'AbortError');
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** Waits for a deferred job to end. Resolves when it completed, throws when it failed. */
async function awaitDeferredJob(
  http: HttpClient,
  jobId: string,
  deadline: number,
  signal?: AbortSignal
): Promise<void> {
  while (Date.now() < deadline) {
    await sleep(JOB_POLL_INTERVAL_MS, signal);
    const res = await http.post<FlexResponse>(
      '/api/tool-recommender/job/status',
      { jobId },
      { signal }
    );
    if (!res.ok) throw new Error(res.message ?? 'errors.generic');
    if (res.data.status === 'completed') return;
    if (res.data.status === 'failed') {
      const { errorMessage } = res.data;
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'errors.generic');
    }
  }
  throw new Error('errors.generic');
}

/**
 * GETs a worker-backed read. A deferred answer is followed until its job is done, then the read
 * is issued again: by then the worker is free and answers within the orchestrator's timeout.
 */
async function readFollowingDeferral(
  http: HttpClient,
  url: string,
  failureMessage: string,
  signal?: AbortSignal
): Promise<unknown> {
  const deadline = Date.now() + JOB_POLL_TIMEOUT_MS;
  for (;;) {
    const res = await http.get<FlexResponse>(url, { signal });
    if (!res.ok) throw new Error(res.message ?? failureMessage);
    const jobId = deferredJobId(res.data);
    if (jobId === null) return res.data;
    await awaitDeferredJob(http, jobId, deadline, signal);
  }
}

export async function trListGaExperiments(
  http: HttpClient,
  signal?: AbortSignal
): Promise<GaExperimentSummary[]> {
  const data = await readFollowingDeferral(
    http,
    '/api/tool-recommender/ga_experiments',
    'Failed to fetch experiments',
    signal
  );
  return normalizeCamel<GaExperimentSummary[]>(extractList(data, 'experiments', 'data', 'items'));
}

export async function trGetGaExperimentResults(
  http: HttpClient,
  experimentId: string,
  signal?: AbortSignal
): Promise<GaExperimentResult> {
  const data = await readFollowingDeferral(
    http,
    `/api/tool-recommender/ga_experiments/${experimentId}/results`,
    'Failed to fetch experiment results',
    signal
  );
  const d = normalizeCamel<Record<string, unknown>>(data);
  if (typeof d.experimentId === 'string') return d as unknown as GaExperimentResult;
  if (d.data && typeof d.data === 'object') return normalizeCamel<GaExperimentResult>(d.data);
  return { experimentId, resultsStored: 0, resultComparisons: [] };
}

export async function trLaunchGaExperiment(
  http: HttpClient,
  input: GaEvaluationInput
): Promise<GaLaunchOutcome> {
  const res = await http.post<LaunchSuccessResponse>('/api/tool-recommender/ga_experiments', input);
  if (!res.ok) return { type: 'error', message: res.message ?? 'errors.generic' };
  const d = normalizeCamel<Record<string, unknown>>(res.data);
  if (typeof d.experimentId === 'string') {
    return { type: 'completed', result: d as unknown as GaExperimentResult };
  }
  // 202 timeout: { name, message, jobId }
  return {
    type: 'timeout',
    jobId: typeof d.jobId === 'string' ? d.jobId : '',
    message: typeof d.message === 'string' ? d.message : '',
  };
}

export async function trGetGaJobStatus(http: HttpClient, jobId: string): Promise<GaLaunchOutcome> {
  const res = await http.post<LaunchSuccessResponse>('/api/tool-recommender/job/status', { jobId });
  if (!res.ok) return { type: 'error', message: res.message ?? 'errors.generic' };
  const d = normalizeCamel<Record<string, unknown>>(res.data);
  const status = d.status as string | undefined;
  if (status === 'completed') {
    return { type: 'completed', result: d as unknown as GaExperimentResult };
  }
  if (status === 'failed') {
    return {
      type: 'error',
      message: typeof d.errorMessage === 'string' ? d.errorMessage : 'errors.generic',
    };
  }
  // pending | processing → still running
  return { type: 'timeout', jobId, message: '' };
}
